import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { opsAPI } from "./opsApi";
import { toast } from "sonner";
import {
  Sparkles, RefreshCw, Bed, Wrench, Package, Users, DollarSign,
  Building2, AlertTriangle, Radar, TrendingUp,
} from "lucide-react";
import CountUp from "./CountUp";
import TodaysFocus from "./TodaysFocus";
import InsightsTimeline from "./InsightsTimeline";
import LiveTicker from "./LiveTicker";
import AskAssistant from "./AskAssistant";
import PredictionsPanel from "./PredictionsPanel";
import Sparkline from "./Sparkline";

const REFRESH_MS = 45_000;
const TREND_COLORS = { occupancy: "#818cf8", ready: "#34d399", cleaned: "#38bdf8", staff: "#fbbf24" };

function currency(v) {
  if (v == null) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
}

export default function OperationsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [live, setLive] = useState(false);
  const [now, setNow] = useState(new Date());
  const [trends, setTrends] = useState(null);
  const esRef = useRef(null);
  const navigate = useNavigate();

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setReloading(true);
    try {
      const d = await opsAPI.missionControl();
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Mission Control");
    } finally { setLoading(false); setReloading(false); }
  }, []);

  // Initial load for instant paint.
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Live SSE stream — server pushes a fresh mission-control every few seconds.
  // Falls back to polling if the stream can't be established.
  useEffect(() => {
    let pollTimer = null;
    let cancelled = false;
    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => fetchAll(true), REFRESH_MS);
    };
    try {
      const es = new EventSource(opsAPI.streamUrl(), { withCredentials: true });
      esRef.current = es;
      es.addEventListener("mission", (ev) => {
        if (cancelled) return;
        try { setData(JSON.parse(ev.data)); setLoading(false); setLive(true); } catch { /* ignore */ }
      });
      es.addEventListener("ping", () => !cancelled && setLive(true));
      es.onerror = () => {
        setLive(false);
        es.close();
        startPolling();       // graceful degradation
      };
    } catch {
      startPolling();
    }
    return () => {
      cancelled = true;
      if (esRef.current) esRef.current.close();
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [fetchAll]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Trend sparklines from the snapshot store.
  const loadTrends = useCallback(async () => {
    try { setTrends(await opsAPI.trends(24)); } catch { /* non-critical */ }
  }, []);
  useEffect(() => {
    loadTrends();
    const t = setInterval(loadTrends, 60_000);
    return () => clearInterval(t);
  }, [loadTrends]);

  const evaluate = async () => {
    setEvaluating(true);
    try {
      await opsAPI.evaluate();
      toast.success("Rules re-evaluated");
      await fetchAll(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setEvaluating(false); }
  };

  const b = data?.briefing;
  const k = data?.kpis || {};
  const insights = data?.insights || [];
  const predictions = insights.filter((i) => i.kind === "prediction")
    .sort((a, c) => (a.horizon_minutes ?? 1e9) - (c.horizon_minutes ?? 1e9));
  const detectiveInsights = insights.filter((i) => i.kind !== "prediction");
  const alerts = data?.alerts || [];

  return (
    <div className="on-dark min-h-full text-white relative overflow-hidden" data-testid="operations-page"
      style={{ background: "#0B1020" }}>
      {/* Mesh gradient background — softened for contrast */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full opacity-[0.18] blur-3xl"
             style={{ background: "radial-gradient(circle, #6366f1 0%, transparent 70%)" }}/>
        <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] rounded-full opacity-[0.14] blur-3xl"
             style={{ background: "radial-gradient(circle, #10b981 0%, transparent 70%)" }}/>
        <div className="absolute -bottom-32 left-1/3 w-[480px] h-[480px] rounded-full opacity-[0.14] blur-3xl"
             style={{ background: "radial-gradient(circle, #f59e0b 0%, transparent 70%)" }}/>
        {/* Dark scrim to guarantee text contrast */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,16,32,0.55) 0%, rgba(11,16,32,0.30) 30%, rgba(11,16,32,0.30) 70%, rgba(11,16,32,0.55) 100%)" }}/>
      </div>

      <div className="relative z-10 p-6 lg:p-8 space-y-5 max-w-[1600px] mx-auto">

        {/* ─── Header ─── */}
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-emerald-300 mb-1.5">
              <Radar className="w-3.5 h-3.5"/> AI Operations Center
            </div>
            <h1 className="font-display text-[32px] sm:text-[38px] font-bold leading-none text-white">
              {b?.greeting || "Good day"},{" "}
              <span className="bg-gradient-to-r from-violet-300 via-fuchsia-300 to-amber-200 bg-clip-text text-transparent">
                {b?.who || "Operator"}
              </span>
            </h1>
            <div className="mt-2 flex items-center gap-4 text-[12.5px] text-white/80">
              <span className="tabular-nums">{now.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
              <span className="w-1 h-1 rounded-full bg-white/40"/>
              <span className="tabular-nums" data-testid="ops-clock">
                {now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
              </span>
              <span className="w-1 h-1 rounded-full bg-white/40"/>
              <span className="inline-flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${live ? "bg-emerald-400 animate-pulse" : data ? "bg-amber-400" : "bg-white/40"}`}/>
                <span className={live ? "text-emerald-300" : "text-amber-200"}>{live ? "Live · streaming" : data ? "Polling" : "Warming up"}</span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={evaluate}
              disabled={evaluating || loading}
              data-testid="ops-evaluate"
              className="h-10 px-4 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] border border-white/[0.14] text-white text-[13px] font-medium inline-flex items-center gap-2 disabled:opacity-50 transition-colors"
            >
              <Sparkles className={`w-4 h-4 ${evaluating ? "animate-spin" : ""}`}/>
              {evaluating ? "Evaluating…" : "Re-evaluate"}
            </button>
            <button
              onClick={() => fetchAll(true)}
              disabled={reloading}
              data-testid="ops-refresh"
              className="h-10 w-10 rounded-xl bg-white/[0.08] hover:bg-white/[0.14] border border-white/[0.14] text-white inline-flex items-center justify-center disabled:opacity-50 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${reloading ? "animate-spin" : ""}`}/>
            </button>
          </div>
        </header>

        {/* ─── Compact AI Briefing Strip ─── */}
        <section
          className="relative rounded-2xl border border-white/[0.14] px-5 py-3.5 flex items-center gap-4"
          style={{
            background: "linear-gradient(90deg, rgba(99,102,241,0.22), rgba(16,185,129,0.12) 60%, rgba(11,16,32,0.6))",
            backdropFilter: "blur(20px)",
          }}
          data-testid="hero-briefing"
        >
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shrink-0 shadow-lg">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2}/>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-violet-200 mb-0.5">AI Briefing</div>
            {loading ? (
              <div className="h-4 rounded bg-white/10 w-3/4 animate-pulse"/>
            ) : (
              <p className="text-[13.5px] text-white leading-snug line-clamp-2" data-testid="briefing-text">{b?.text}</p>
            )}
          </div>
        </section>

        {/* ═══════════════════════════════════════════════════════════
            CENTERPIECE — Today's Focus
            ═══════════════════════════════════════════════════════════ */}
        <TodaysFocus
          data={data}
          onInsightAcked={() => fetchAll(true)}
          onNavigate={(to) => to && navigate(to)}
        />

        {/* ─── Live Ticker ─── */}
        <LiveTicker items={alerts}/>

        {/* ═══ LIVE TRENDS — sparklines over the snapshot store ═══ */}
        {trends?.metrics && (
          <section
            className="rounded-3xl border border-white/[0.14] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden"
            data-testid="trends-strip"
          >
            <div className="px-5 py-3.5 border-b border-white/[0.10] flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
                  <TrendingUp className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="font-display text-[16px] font-semibold text-white leading-tight">Live Trends</h2>
                  <p className="text-[12px] text-white/70">Last 24 hours · sampled continuously</p>
                </div>
              </div>
            </div>
            <div className="p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
              {["occupancy", "ready", "cleaned", "staff"].map((key) => (
                <Sparkline key={key} metric={trends.metrics[key]} color={TREND_COLORS[key]} testid={`trend-${key}`} />
              ))}
            </div>
          </section>
        )}

        {/* ═══════════════════════════════════════════════════════════
            PREDICTIVE EARLY WARNINGS — the forward-looking layer
            ═══════════════════════════════════════════════════════════ */}
        <PredictionsPanel predictions={predictions} onChanged={() => { fetchAll(true); loadTrends(); }} />

        {/* ─── Compact KPI strip (secondary) ─── */}
        <section
          className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-2"
          data-testid="mission-grid"
        >
          <MiniStat testid="kpi-revenue" icon={DollarSign} accent="#10b981" label="Revenue"
            value={<span>{k.todays_revenue != null ? currency(k.todays_revenue) : "—"}</span>}
            sub={k.adr ? `ADR ${currency(k.adr)}` : ""}/>
          <MiniStat testid="kpi-occupancy" icon={Bed} accent="#38bdf8" label="Occupancy"
            value={<span><CountUp value={k.occupancy_pct || 0} decimals={0}/>%</span>}
            sub={`${k.occupied || 0}/${k.total_rooms || 0}`}/>
          <MiniStat testid="kpi-housekeeping" icon={Sparkles} accent="#f59e0b" label="Dirty"
            value={<CountUp value={k.rooms_dirty || 0}/>}
            sub={`${k.rooms_ready || 0} ready`}/>
          <MiniStat testid="kpi-frontoffice" icon={Users} accent="#a78bfa" label="FO Queue"
            value={<CountUp value={(k.pending_checkins || 0) + (k.pending_checkouts || 0)}/>}
            sub={`${k.vip_arrivals_today || 0} VIP`}/>
          <MiniStat testid="kpi-maintenance" icon={Wrench} accent="#fb923c" label="Eng"
            value={<CountUp value={k.engineering_tickets || 0}/>}
            sub={`${k.rooms_out_of_order || 0} OOO`}/>
          <MiniStat testid="kpi-inventory" icon={Package} accent="#e879f9" label="Inventory"
            value={<span className="text-white/40 italic">·</span>} sub="Phase 2"/>
          <MiniStat testid="kpi-workforce" icon={Building2} accent="#10b981" label="Staff"
            value={<CountUp value={k.staff_online || 0}/>} sub="on shift"/>
          <MiniStat testid="kpi-alerts" icon={AlertTriangle}
            accent={insights.filter((i)=>i.severity==="critical"||i.severity==="high").length ? "#f43f5e" : "#94a3b8"}
            label="Attention"
            value={<CountUp value={insights.filter((i)=>i.severity==="critical"||i.severity==="high").length}/>}
            sub={`${insights.length} total`}/>
        </section>

        {/* ─── Insights + Recommendations ─── */}
        <section className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-5">
          <div className="rounded-3xl border border-white/[0.14] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden" data-testid="insights-panel">
            <div className="px-5 py-4 border-b border-white/[0.10] flex items-baseline justify-between">
              <div>
                <h2 className="font-display text-[16px] font-semibold text-white">AI Insights</h2>
                <p className="text-[12px] text-white/70">Live issues detected now · rule engine</p>
              </div>
              <span className="text-[11px] uppercase tracking-widest text-white/70 font-semibold">{detectiveInsights.length} open</span>
            </div>
            <div className="p-4 max-h-[520px] overflow-y-auto">
              <InsightsTimeline insights={detectiveInsights} onChanged={() => fetchAll(true)}/>
            </div>
          </div>

          <div className="rounded-3xl border border-white/[0.14] bg-[#0F1530]/70 backdrop-blur-md overflow-hidden" data-testid="recommendations-panel">
            <div className="px-5 py-4 border-b border-white/[0.10]">
              <h2 className="font-display text-[16px] font-semibold text-white">Recommended Actions</h2>
              <p className="text-[12px] text-white/70">One tap to jump into the module</p>
            </div>
            <div className="p-4 space-y-2 max-h-[520px] overflow-y-auto">
              {(data?.recommendations || []).length === 0 ? (
                <div className="text-center py-8 text-[13px] text-white/70">Nothing pending — you&apos;re on top of things.</div>
              ) : (data?.recommendations || []).map((r) => (
                <div key={r.insight_id} data-testid={`recommendation-${r.insight_id}`}
                  className="rounded-2xl bg-white/[0.06] border border-white/[0.10] px-4 py-3 hover:bg-white/[0.10] transition-colors">
                  <div className="flex items-start gap-3">
                    <div className={`w-2 h-2 mt-1.5 rounded-full ${
                      r.severity === "critical" ? "bg-red-400"
                        : r.severity === "high" ? "bg-amber-400"
                        : r.severity === "medium" ? "bg-sky-400"
                        : "bg-emerald-400"}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-white font-medium truncate">{r.title}</div>
                      <div className="text-[12px] text-white/80 mt-0.5 line-clamp-2">{r.recommendation}</div>
                      <div className="mt-1.5 flex items-center gap-2 text-[10.5px] text-white/60 uppercase tracking-wider">
                        <span>{r.module}</span>
                        <span>·</span>
                        <span className="tabular-nums">Confidence {Math.round((r.confidence || 0) * 100)}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <AskAssistant/>
    </div>
  );
}


/* ───────────── Mini-stat pill for the secondary KPI strip ───────────── */
function MiniStat({ icon: Icon, accent, label, value, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-white/[0.14] bg-[#0F1530]/70 hover:bg-[#141C3D]/80 backdrop-blur-md transition-colors px-3.5 py-3 flex flex-col justify-between min-h-[96px]"
    >
      <div className="flex items-center justify-between text-[10.5px] uppercase tracking-widest text-white/80 font-semibold">
        <span>{label}</span>
        <Icon className="w-3.5 h-3.5" strokeWidth={2} style={{ color: accent }}/>
      </div>
      <div className="font-display font-bold text-white text-[22px] leading-none tabular-nums">{value}</div>
      <div className="text-[10.5px] text-white/60 truncate">{sub}</div>
    </div>
  );
}
